import { PackageWorkflowClient } from '@frontend/package/api';
import { PackageTriggerName } from '@frontend/package/types';
import { IProduct } from '@frontend/product/types';
import { ReviewClient } from '@frontend/review/api';
import { ReviewOutcome } from '@frontend/review/types';
import { ScreenTrigger } from '@frontend/wizard/types';
import { useState } from 'react';

import { useWizardPackage } from '../../hooks/useWizardPackage';
import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { useScreen } from '../useScreen';

interface ViewProps {
    product?: IProduct;
    isProcessing: boolean;
    stopProcessing: () => void;
    selectedOption: ReviewOutcome | null;
    setSelectedOption: (option: ReviewOutcome) => void;

    addToStock: boolean;
    toggleAddToStock: () => void;

    handleSubmit: () => void;
}

const useVendingReviewResolve = (): ViewProps => {
    const { store, wizardId, wizardData, screen_type, onSubmit, navigationState } = useScreen();
    const { product } = wizardData;
    const [isProcessing, setIsProcessing] = useState(false);
    const [addToStock, setAddToStock] = useState<boolean>(true);
    const [selectedOption, setSelectedOptionState] = useState<ReviewOutcome | null>(null);

    const { transaction } = useWizardTransaction({
        store,
        wizardId
    });
    const { package: _package } = useWizardPackage({
        store,
        wizardId
    });

    const handleSubmit = async () => {
        if (!selectedOption || !transaction || !_package || !_package.account_id || !_package.transaction_id || !_package.id) return;
        const shouldAddToStock = selectedOption === ReviewOutcome.OK && addToStock;
        setIsProcessing(true);
        try {
            await ReviewClient.postReview(_package.account_id, {
                outcome: selectedOption,
                product_id: product?.id ?? '',
                application: store?.functionality.application,
                feature: store?.functionality.feature,
                expected: 1,
                actual: selectedOption === ReviewOutcome.MISSING ? 0 : 1,
                user_id: navigationState?.user?.id
            });
            await PackageWorkflowClient.triggerPackageState(_package.account_id, _package.transaction_id, _package.id, {
                trigger: shouldAddToStock ? PackageTriggerName.PACKAGE_DROPPED_OFF : PackageTriggerName.PACKAGE_AUDITED,
                location: navigationState.spot?.id ?? undefined,
                actor: navigationState.user?.id ?? undefined
            });
            const subFalseStep = store?.steps.find((step) => step.screen === screen_type && step.trigger === ScreenTrigger.SUBMIT_FALSE);
            if (!shouldAddToStock && subFalseStep) {
                onSubmit(subFalseStep);
            } else onSubmit();
        } finally {
            setIsProcessing(false);
        }
    };

    return {
        product,
        isProcessing,
        stopProcessing: () => setIsProcessing(false),
        selectedOption,
        setSelectedOption: (option) => {
            setSelectedOptionState(option);
            setAddToStock(option === ReviewOutcome.OK);
        },
        addToStock,
        toggleAddToStock: () => {
            setAddToStock((prev) => !prev);
        },
        handleSubmit
    };
};

export default useVendingReviewResolve;
